import { DataTable, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listNotificationLog } from "../../api/notificationsApi";
import type { NotificationLogOut } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const COLUMNS: DataTableColumn<NotificationLogOut, keyof NotificationLogOut>[] = [
  { key: "created_at", header: "When", render: (r) => new Date(r.created_at).toLocaleString() },
  { key: "channel", header: "Channel", render: (r) => r.channel },
  { key: "to", header: "To", render: (r) => r.to },
  { key: "subject", header: "Subject", render: (r) => r.subject ?? "—" },
  { key: "status", header: "Status", render: (r) => <span className="badge badge--due-soon">{r.status}</span> },
];

/**
 * Notifications Log — `GET /notifications/log` (§12.5), OWNER-only, read-only.
 * **Every row is `status = "LOGGED_NOT_SENT"`** — this backend has no real email/SMS
 * provider wired up (`LogNotificationProvider` is the only implementation), so this page
 * is honestly framed as "what would have been sent," not a real delivery log.
 */
export function NotificationsLogPage() {
  const { user, ensureFreshToken } = useAuth();
  const isOwner = user?.role === "OWNER";
  const logState = useAsyncData(() => ensureFreshToken().then((token) => listNotificationLog(token)), []);
  const { pageData: logPageData, pagination: logPagination, setPage: setLogPage } = usePagination(logState.data ?? [], 25);

  if (!isOwner) {
    return (
      <div className="page">
        <div className="page__header">
          <div>
            <h1 className="page__title">Notifications Log</h1>
          </div>
        </div>
        <StatusBanner kind="info">The notifications log is OWNER-only. Your role ({user?.role}) doesn't have access.</StatusBanner>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Notifications Log</h1>
          <p className="page__subtitle">
            An append-only record of what would have been sent. No real email/SMS provider is wired up in
            this backend — every row here is <code>LOGGED_NOT_SENT</code>, not an actual delivery.
          </p>
        </div>
      </div>

      {logState.error ? <StatusBanner kind="error">{logState.error}</StatusBanner> : null}

      <div className="panel">
        {logState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={logPageData}
            getRowId={(row) => row.id}
            pagination={logPagination}
            onPageChange={setLogPage}
            emptyMessage="No notifications logged yet."
            caption="Notifications Log"
          />
        )}
      </div>
    </div>
  );
}
